import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, type Brand, type Campaign } from "../../lib/api";

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS: { value: string; label: string; color: string }[] = [
  { value: "draft", label: "Draft", color: "bg-gray-100 text-slate" },
  { value: "active", label: "Active", color: "bg-green-100 text-green-700" },
  { value: "paused", label: "Paused", color: "bg-amber-100 text-amber-700" },
  {
    value: "completed",
    label: "Completed",
    color: "bg-brand-100 text-brand-700",
  },
];

const PLATFORM_LABELS: Record<string, string> = {
  instagram_feed: "IG Feed",
  instagram_story: "IG Story",
  linkedin: "LinkedIn",
  twitter: "X",
  facebook: "Facebook",
  bluesky: "Bluesky",
};

const ACTIVE_JOBS_KEY = "md_active_jobs";

interface ActiveJob {
  jobId: string;
  campaignId: string;
  campaignName: string;
  brandId: string;
}

type JobStatus = "queued" | "processing" | "completed" | "failed";

interface CampaignGenState {
  status: JobStatus;
  postsCreated: number;
  error: string | null;
}

function readActiveJobs(): ActiveJob[] {
  try {
    return JSON.parse(localStorage.getItem(ACTIVE_JOBS_KEY) ?? "[]");
  } catch {
    return [];
  }
}

function writeActiveJobs(jobs: ActiveJob[]): void {
  localStorage.setItem(ACTIVE_JOBS_KEY, JSON.stringify(jobs));
}

// ─── Toast notification ───────────────────────────────────────────────────────

function Toast({
  message,
  action,
  onDismiss,
}: {
  message: string;
  action?: { label: string; href: string };
  onDismiss: () => void;
}) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 8000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom">
      <div className="bg-gray-900 text-white rounded-xl shadow-2xl px-5 py-3.5 flex items-center gap-3 max-w-sm">
        <span className="text-green-400 flex-shrink-0">✓</span>
        <span className="text-sm flex-1">{message}</span>
        {action && (
          <Link
            to={action.href}
            className="text-sm font-semibold text-brand-300 hover:text-brand-200 whitespace-nowrap"
          >
            {action.label} →
          </Link>
        )}
        <button
          onClick={onDismiss}
          className="text-faint hover:text-white ml-1 flex-shrink-0"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function CampaignList() {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const [brand, setBrand] = useState<Brand | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dispatching, setDispatching] = useState<string | null>(null);

  // Inline menus (one open at a time)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [duplicating, setDuplicating] = useState<string | null>(null);

  // Toast
  const [toast, setToast] = useState<{
    message: string;
    action?: { label: string; href: string };
  } | null>(null);

  // Per-campaign generation status (campaignId → state)
  const [genStatus, setGenStatus] = useState<Record<string, CampaignGenState>>(
    {},
  );
  const pollIntervals = useRef<Record<string, ReturnType<typeof setInterval>>>(
    {},
  );

  // Close menu when clicking outside
  useEffect(() => {
    if (!openMenuId) return;
    const handler = () => setOpenMenuId(null);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [openMenuId]);

  useEffect(() => {
    if (!brandId) return;
    Promise.all([
      api.brands.get(brandId).then((r) => setBrand(r.brand)),
      api.campaigns.list(brandId).then((r) => setCampaigns(r.campaigns)),
    ])
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [brandId]);

  // Resume polling for any jobs that were in-progress when the page was last viewed
  useEffect(() => {
    if (!brandId) return;
    const stored = readActiveJobs().filter((j) => j.brandId === brandId);
    const intervals = pollIntervals.current;
    for (const job of stored) {
      startPolling(job);
    }
    return () => {
      for (const id of Object.keys(intervals)) {
        clearInterval(intervals[id]);
      }
    };
  }, [brandId]);

  const startPolling = (job: ActiveJob) => {
    if (pollIntervals.current[job.campaignId]) return;

    setGenStatus((prev) => ({
      ...prev,
      [job.campaignId]: { status: "queued", postsCreated: 0, error: null },
    }));

    const interval = setInterval(async () => {
      try {
        const result = await api.generate.status(job.jobId);
        const status = result.status as JobStatus;

        setGenStatus((prev) => ({
          ...prev,
          [job.campaignId]: {
            status,
            postsCreated: result.postsCreated ?? 0,
            error: result.error ?? null,
          },
        }));

        if (status === "completed" || status === "failed") {
          clearInterval(pollIntervals.current[job.campaignId]);
          delete pollIntervals.current[job.campaignId];
          const remaining = readActiveJobs().filter(
            (j) => j.jobId !== job.jobId,
          );
          writeActiveJobs(remaining);

          // Show toast on completion
          if (status === "completed") {
            setToast({
              message: `${result.postsCreated ?? 0} posts ready for "${job.campaignName}"`,
              action: {
                label: "Review posts",
                href: `/review?campaignId=${job.campaignId}`,
              },
            });
          }
        }
      } catch {
        // Silently swallow poll errors — will retry next tick
      }
    }, 2000);

    pollIntervals.current[job.campaignId] = interval;
  };

  // ── Actions ─────────────────────────────────────────────────────────────────

  const handleGenerate = async (campaign: Campaign) => {
    if (!brandId) return;
    setDispatching(campaign.campaignId);
    try {
      const { jobId } = await api.generate.dispatch({
        brandId,
        campaignId: campaign.campaignId,
        platforms: campaign.platforms,
        variationsCount: 5,
      });

      const job: ActiveJob = {
        jobId,
        campaignId: campaign.campaignId,
        campaignName: campaign.name,
        brandId,
      };

      const existing = readActiveJobs().filter(
        (j) => j.campaignId !== campaign.campaignId,
      );
      writeActiveJobs([...existing, job]);
      startPolling(job);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to dispatch generation");
    } finally {
      setDispatching(null);
    }
  };

  const handleDelete = async (campaign: Campaign) => {
    if (!brandId) return;
    try {
      await api.campaigns.delete(brandId, campaign.campaignId);
      setCampaigns((prev) =>
        prev.filter((c) => c.campaignId !== campaign.campaignId),
      );
      setConfirmDeleteId(null);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Delete failed");
    }
  };

  const handleDuplicate = async (campaign: Campaign) => {
    if (!brandId) return;
    setDuplicating(campaign.campaignId);
    try {
      const { campaign: created } = await api.campaigns.create(brandId, {
        name: `${campaign.name} (copy)`,
        objective: campaign.objective,
        product: campaign.product,
        platforms: campaign.platforms,
        keyMessages: campaign.keyMessages,
        startDate: campaign.startDate,
        endDate: campaign.endDate,
        cadence: campaign.cadence,
        ctas: campaign.ctas,
        assetR2Keys: campaign.assetR2Keys,
        includeLinkBacks: campaign.includeLinkBacks,
      });
      setCampaigns((prev) => [created, ...prev]);
      setToast({ message: `"${created.name}" created` });
    } catch (e) {
      alert(e instanceof Error ? e.message : "Duplicate failed");
    } finally {
      setDuplicating(null);
    }
  };

  const handleStatusChange = async (campaign: Campaign, newStatus: string) => {
    if (!brandId) return;
    try {
      const { campaign: updated } = await api.campaigns.update(
        brandId,
        campaign.campaignId,
        { status: newStatus } as Partial<Campaign>,
      );
      setCampaigns((prev) =>
        prev.map((c) => (c.campaignId === campaign.campaignId ? updated : c)),
      );
    } catch (e) {
      alert(e instanceof Error ? e.message : "Status update failed");
    }
  };

  // ── Generation badge ────────────────────────────────────────────────────────

  const renderGenBadge = (campaign: Campaign) => {
    const state = genStatus[campaign.campaignId];
    if (!state) return null;

    if (state.status === "queued" || state.status === "processing") {
      return (
        <div className="flex items-center gap-1.5 text-xs text-brand-600 mt-2">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-brand-400 animate-pulse flex-shrink-0" />
          <span>
            {state.status === "queued"
              ? "Queued — starting shortly…"
              : "AI is writing posts…"}
          </span>
        </div>
      );
    }

    if (state.status === "completed") {
      return (
        <div className="flex items-center gap-1.5 text-xs text-green-700 mt-2">
          <span className="text-green-500">✓</span>
          <span>
            {state.postsCreated} post{state.postsCreated !== 1 ? "s" : ""} ready
          </span>
          <Link
            to={`/review?campaignId=${campaign.campaignId}`}
            className="font-semibold underline hover:text-green-900 transition-colors ml-1"
          >
            Review →
          </Link>
        </div>
      );
    }

    if (state.status === "failed") {
      return (
        <div className="flex items-center gap-1.5 text-xs text-red-600 mt-2">
          <span>✕</span>
          <span>Failed{state.error ? `: ${state.error}` : ""}. Try again.</span>
        </div>
      );
    }

    return null;
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const statusMeta = (s: string) =>
    STATUS_OPTIONS.find((o) => o.value === s) ?? STATUS_OPTIONS[0];

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <button
            onClick={() => navigate("/brands")}
            className="flex items-center gap-1.5 text-sm text-faint hover:text-slate mb-2 transition-colors"
            aria-label="Back to brands"
          >
            ← Brands
          </button>
          <h1 className="page-title">Campaigns</h1>
          {brand && (
            <p className="text-sm text-muted mt-0.5">
              for <span className="font-medium text-slate">{brand.name}</span>
            </p>
          )}
        </div>
        <Link
          to={`/brands/${brandId}/campaigns/new`}
          className="btn-primary mt-7 whitespace-nowrap"
        >
          + New campaign
        </Link>
      </div>

      {/* Workflow steps — step 2 active */}
      <div className="flex flex-col sm:flex-row items-stretch gap-0 mb-6 rounded-xl overflow-hidden border border-gray-200 bg-white">
        <div className="flex-1 px-4 py-3 bg-gray-50">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">
              ✓
            </span>
            <span className="text-sm font-semibold text-muted">
              Brand created
            </span>
          </div>
          <p className="text-xs text-faint ml-7">
            {brand ? brand.name : "Your brand identity"}
          </p>
        </div>
        <div className="hidden sm:flex items-center px-2 bg-gray-50 text-gray-300 text-sm font-light select-none">
          ›
        </div>
        <div className="flex-1 px-4 py-3 bg-brand-500 text-white">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="bg-white text-brand-600 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">
              2
            </span>
            <span className="text-sm font-semibold">Set up a campaign</span>
          </div>
          <p className="text-xs text-white/80 ml-7">
            Define goals, platforms & schedule
          </p>
        </div>
        <div className="hidden sm:flex items-center px-2 bg-gray-50 text-gray-300 text-sm font-light select-none">
          ›
        </div>
        <div className="flex-1 px-4 py-3 bg-gray-50">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="border border-gray-300 text-faint rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">
              3
            </span>
            <span className="text-sm font-semibold text-muted">
              Generate & review
            </span>
          </div>
          <p className="text-xs text-faint ml-7">
            AI writes posts; you approve or edit
          </p>
        </div>
      </div>

      {/* What is a campaign? */}
      <div className="bg-brand-50 border border-brand-100 rounded-xl px-5 py-4 mb-8">
        <p className="text-sm font-semibold text-brand-800 mb-1">
          What is a campaign?
        </p>
        <p className="text-sm text-brand-700 leading-relaxed">
          A campaign tells the AI <em>what</em> to write about — the product or
          offer, the goal (awareness, conversions, etc.), the target platforms,
          and the date range. Once you create one, hit{" "}
          <strong>⚡ Generate</strong> and the AI will produce a batch of
          ready-to-review posts using your brand's voice and colors.
        </p>
      </div>

      {loading && <p className="text-faint text-sm">Loading…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!loading && campaigns.length === 0 && (
        <div className="card p-10 text-center">
          <div className="w-12 h-12 rounded-full bg-brand-50 flex items-center justify-center mx-auto mb-4 text-2xl">
            📋
          </div>
          <p className="font-semibold text-ink mb-1">No campaigns yet</p>
          <p className="text-sm text-muted mb-2 max-w-sm mx-auto">
            Create your first campaign to define what the AI should write about
            — the product, goal, and platforms.
          </p>
          <p className="text-xs text-faint mb-6">
            Once created, click <strong>⚡ Generate</strong> to produce a batch
            of posts for review.
          </p>
          <Link to={`/brands/${brandId}/campaigns/new`} className="btn-primary">
            Create your first campaign
          </Link>
        </div>
      )}

      {/* ── Campaign list ── */}
      <div className="space-y-3">
        {campaigns.map((c) => {
          const meta = statusMeta(c.status);
          const isGenerating =
            !!genStatus[c.campaignId]?.status &&
            ["queued", "processing"].includes(genStatus[c.campaignId].status);
          const isConfirmingDelete = confirmDeleteId === c.campaignId;

          return (
            <div key={c.campaignId} className="card p-5">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
                {/* Left: campaign info */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-ink truncate">{c.name}</p>

                    {/* Status dropdown */}
                    <div className="relative flex-shrink-0">
                      <select
                        value={c.status}
                        onChange={(e) => handleStatusChange(c, e.target.value)}
                        className={`text-xs pl-2 pr-6 py-0.5 rounded-full capitalize font-medium border-0 cursor-pointer appearance-none ${meta.color}`}
                        style={{
                          backgroundImage:
                            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M3 5l3 3 3-3'/%3E%3C/svg%3E\")",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "right 4px center",
                        }}
                        aria-label="Change campaign status"
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Meta line: objective + dates */}
                  <p className="text-sm text-muted capitalize">
                    {c.objective}
                    {c.startDate ? ` · ${c.startDate}` : ""}
                    {c.endDate ? ` → ${c.endDate}` : ""}
                  </p>

                  {/* Platforms */}
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    {c.platforms.map((p) => (
                      <span
                        key={p}
                        className="text-xs bg-gray-100 text-slate px-2 py-0.5 rounded-full"
                      >
                        {PLATFORM_LABELS[p] ?? p}
                      </span>
                    ))}
                  </div>

                  {/* Generation status */}
                  {renderGenBadge(c)}
                </div>

                {/* Right: actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Generate */}
                  <button
                    onClick={() => handleGenerate(c)}
                    disabled={!!dispatching || isGenerating}
                    className="btn-primary text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Generate a batch of AI-written posts"
                  >
                    {dispatching === c.campaignId
                      ? "Queuing…"
                      : "⚡ Generate posts"}
                  </button>

                  {/* Edit */}
                  <Link
                    to={`/brands/${brandId}/campaigns/${c.campaignId}/edit`}
                    className="btn-secondary text-xs"
                  >
                    Edit
                  </Link>

                  {/* Overflow menu (three dots) */}
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuId(
                          openMenuId === c.campaignId ? null : c.campaignId,
                        );
                      }}
                      className="text-faint hover:text-slate p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                      aria-label="More actions"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                      >
                        <circle cx="8" cy="3" r="1.5" />
                        <circle cx="8" cy="8" r="1.5" />
                        <circle cx="8" cy="13" r="1.5" />
                      </svg>
                    </button>

                    {openMenuId === c.campaignId && (
                      <div
                        className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg py-1 w-44 z-20"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Duplicate */}
                        <button
                          onClick={() => {
                            setOpenMenuId(null);
                            void handleDuplicate(c);
                          }}
                          disabled={duplicating === c.campaignId}
                          className="w-full text-left px-4 py-2 text-sm text-slate hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
                        >
                          <span className="text-faint">⧉</span>
                          {duplicating === c.campaignId
                            ? "Duplicating…"
                            : "Duplicate"}
                        </button>

                        {/* Pause / Resume */}
                        {c.status === "active" ? (
                          <button
                            onClick={() => {
                              setOpenMenuId(null);
                              void handleStatusChange(c, "paused");
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-slate hover:bg-gray-50 flex items-center gap-2"
                          >
                            <span className="text-amber-500">⏸</span>
                            Pause
                          </button>
                        ) : c.status === "paused" ? (
                          <button
                            onClick={() => {
                              setOpenMenuId(null);
                              void handleStatusChange(c, "active");
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-slate hover:bg-gray-50 flex items-center gap-2"
                          >
                            <span className="text-green-500">▶</span>
                            Resume
                          </button>
                        ) : null}

                        {/* Delete */}
                        <div className="border-t border-gray-100 mt-1 pt-1">
                          <button
                            onClick={() => {
                              setOpenMenuId(null);
                              setConfirmDeleteId(c.campaignId);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          >
                            <span>🗑</span>
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Delete confirmation inline */}
              {isConfirmingDelete && (
                <div className="mt-3 pt-3 border-t border-red-100 bg-red-50 -mx-5 -mb-5 px-5 py-4 rounded-b-xl flex items-center justify-between gap-3">
                  <p className="text-sm text-red-800">
                    Delete <strong>{c.name}</strong>? This cannot be undone.
                  </p>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => void handleDelete(c)}
                      className="text-xs font-medium bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      className="text-xs font-medium text-slate px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-white transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Toast notification */}
      {toast && (
        <Toast
          message={toast.message}
          action={toast.action}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}
